// src/components/SEOConfig.tsx
import { Helmet } from 'react-helmet-async';

interface SEOConfigProps {
  title?: string;
  description?: string;
  canonicalPath?: string;
  cityStats?: {
    total: number;
    qualityStats?: {
      avgRatingTopStores: number;
    };
  } | null;
  type?: 'website' | 'article';
  imageUrl?: string;
}

const DEFAULT_TITLE = 'Bookmap City - Find Independent Bookstores';
const DEFAULT_DESCRIPTION = 'Discover and explore independent bookstores across major cities. Find local bookshops, literary clusters, and explore reading culture in your area.';
const BASE_URL = 'https://bookmap.city';

export function SEOConfig({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESCRIPTION,
  canonicalPath = '',
  cityStats,
  type = 'website',
  imageUrl = '/assets/default-social.jpg'
}: SEOConfigProps) {
  const fullTitle = title === DEFAULT_TITLE ? title : `${title} - Bookmap City`;
  const canonicalUrl = `${BASE_URL}${canonicalPath}`;
  
  // Generate structured data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Bookmap City",
    "applicationCategory": "MapApplication",
    "description": description,
    "url": canonicalUrl,
    "image": `${BASE_URL}${imageUrl}`,
    "author": {
      "@type": "Person",
      "name": "Giorgio Baldelli"
    },
    ...(cityStats && cityStats.total > 0 && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": cityStats.qualityStats?.avgRatingTopStores || 0,
        "ratingCount": cityStats.total || 0,
        "bestRating": "5",
        "worstRating": "1"
      }
    })
  };

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* OpenGraph Meta Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={`${BASE_URL}${imageUrl}`} />
      <meta property="og:site_name" content="Bookmap City" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={`${BASE_URL}${imageUrl}`} />
      
      {/* Additional Meta Tags */}
      <meta name="application-name" content="Bookmap City" />
      <meta name="apple-mobile-web-app-title" content="Bookmap City" />
      <meta name="theme-color" content="#18181B" />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
}